/**
 * Resolves an icon form value to a static asset path for library icons.
 *
 * Returns a relative URL string (`assets/icons/lib/…`) when the icon comes
 * from a bundled library (phosphor | lucide | material), or `null` when it
 * is a media-library icon (callers then fall back to their existing media
 * resolution logic).
 *
 * Backward-compatible: values without an explicit `library` field are treated
 * as `'media'` and return `null`.
 */

const DEFAULT_VARIANTS: Record<string, string> = {
  phosphor: 'regular',
  lucide: 'regular',
  material: 'outlined',
}

export type IconLibraryName = 'phosphor' | 'lucide' | 'material' | 'media'

export interface IconLibraryValue {
  value: string
  library?: IconLibraryName
  variant?: string
  appId?: string | null
  resDefId?: string | null
}

/**
 * Returns a path suitable for `<flx-icon [path]="...">` for library icons,
 * or `null` for media-library / legacy icons.
 */
export function getIconLibraryPath(icon: IconLibraryValue | null | undefined): string | null {
  if (!icon?.value) {
    return null
  }

  const library = icon.library ?? 'media'
  if (library === 'media') {
    return null
  }

  const variant = icon.variant ?? DEFAULT_VARIANTS[library] ?? 'regular'
  return `assets/icons/lib/${library}/${variant}/${icon.value}.svg`
}
